// Mietverhältnis bearbeiten (Tabelle mieter) – reine Funktionen für das Formular im neuen Design.
// Speicherlogik 1:1 wie im früheren Mietverhältnis-Dialog (bis Okt. 2026 MietverhaeltnisModal.tsx):
// dieselben Felder, dieselbe Zahlen- und Datumsbehandlung, Korrektur des aktuell gültigen Mietstands.

import {
  aktuellerStand, leseStand, mitAnfangsstand, sortiereStaende, spiegelFelder, summePositionen, type Mietstand,
} from '../mieter/mietstaende';

/** Textfelder des Formulars (alles als Text bearbeitet, Zahlen werden beim Speichern gelesen). */
export interface MvEntwurf {
  vorname: string; nachname: string; firma_name: string; email: string; telefon: string; mobil: string;
  mieter_iban: string; mieter_kontoinhaber: string;
  status: string; vertragsart: string; mietbeginn: string; mietende: string;
  miete_anteilig_beginn: boolean; miete_anteilig_ende: boolean;
  miete_faellig_tag: string; mietintervall: string; nur_werktage: boolean; sollstellung_vormonat: boolean;
  anzahl_bewohner: string; notizen: string;
  kaltmiete: string; nebenkosten_vorauszahlung: string; heizkosten: string; weitere_kosten: string; mwst: boolean;
  kaution: string; kaution_art: string; kaution_faelligkeit: string;
  anfangssaldo_aktiv: boolean; anfangssaldo_faelligkeit: string; anfangssaldo_betrag: string;
  sepa_aktiv: boolean; sepa_mandatsreferenz: string; sepa_mandatsdatum: string;
  einzug_zaehler_gas: string; einzug_zaehler_strom: string; einzug_notizen: string;
  auszug_zaehler_gas: string; auszug_zaehler_strom: string; auszug_notizen: string;
}

export const VERTRAGSARTEN = ['Unbefristeter Mietvertrag', 'Befristeter Mietvertrag', 'Staffelmiete', 'Indexmiete'] as const;
export const MIETINTERVALLE = ['Monatlich', 'Quartalsweise', 'Halbjährlich', 'Jährlich'] as const;
export const KAUTIONSARTEN = ['Bürgschaft', 'Bar', 'Überweisung', 'Sonstiges'] as const;
/** Status des Mietverhältnisses; aktiv und gekündigt gelten als „wohnend“ (istWohnend). */
export const MIETER_STATUS = [
  { key: 'aktiv', label: 'Aktiv' }, { key: 'gekündigt', label: 'Gekündigt' }, { key: 'ehemalig', label: 'Ehemalig' }, { key: 'inaktiv', label: 'Inaktiv' },
] as const;

const TEXT_FELDER = [
  'vorname', 'nachname', 'firma_name', 'email', 'telefon', 'mobil', 'mieter_iban', 'mieter_kontoinhaber',
  'status', 'vertragsart', 'mietbeginn', 'mietende', 'miete_faellig_tag', 'mietintervall', 'anzahl_bewohner', 'notizen',
  'kaltmiete', 'nebenkosten_vorauszahlung', 'heizkosten', 'weitere_kosten',
  'kaution', 'kaution_art', 'kaution_faelligkeit', 'anfangssaldo_faelligkeit', 'anfangssaldo_betrag',
  'sepa_mandatsreferenz', 'sepa_mandatsdatum',
  'einzug_zaehler_gas', 'einzug_zaehler_strom', 'einzug_notizen', 'auszug_zaehler_gas', 'auszug_zaehler_strom', 'auszug_notizen',
] as const;
const SCHALTER = ['miete_anteilig_beginn', 'miete_anteilig_ende', 'nur_werktage', 'sollstellung_vormonat', 'mwst', 'anfangssaldo_aktiv', 'sepa_aktiv'] as const;

/** Zahlenfelder wie bisher (NUMERIC_FIELDS). */
export const ZAHL_FELDER = [
  'kaltmiete', 'nebenkosten_vorauszahlung', 'heizkosten', 'weitere_kosten', 'kaution', 'anfangssaldo_betrag',
  'einzug_zaehler_gas', 'einzug_zaehler_strom', 'auszug_zaehler_gas', 'auszug_zaehler_strom', 'miete_faellig_tag', 'anzahl_bewohner',
] as const;

const text = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'number' ? String(v).replace('.', ',') : String(v));
const datum = (v: unknown): string => (typeof v === 'string' ? v.slice(0, 10) : '');

/** Wie bisher: Ziffern, Komma, Punkt behalten; mit Komma ist der Punkt Tausendertrenner. Leer → null. */
export function leseZahl(v: string): number | null {
  let s = v.replace(/[^0-9,.]/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export interface MvStart { entwurf: MvEntwurf; staende: Mietstand[]; kautionAn: boolean }

/**
 * Formular aus der mieter-Zeile: Mietstände normalisieren (Anfangsstand ergänzen) und die vier
 * Mietfelder auf den heute gültigen Stand setzen – wie beim Öffnen des bisherigen Dialogs.
 */
export function startAusMieter(m: Record<string, unknown>, heute: string): MvStart {
  const roh = Array.isArray(m.miet_historie) ? m.miet_historie : [];
  const start = (text(m.mietbeginn) || text(m.created_at)).slice(0, 10);
  const staende = mitAnfangsstand(roh.map((r, i) => leseStand(r, i)), m, start);
  const akt = aktuellerStand(staende, heute);
  const quelle: Record<string, unknown> = akt ? { ...m, ...spiegelFelder(akt) } : m;
  const e = {} as Record<string, string | boolean>;
  for (const f of TEXT_FELDER) e[f] = ['mietbeginn', 'mietende', 'kaution_faelligkeit', 'anfangssaldo_faelligkeit', 'sepa_mandatsdatum'].includes(f) ? datum(quelle[f]) : text(quelle[f]);
  for (const f of SCHALTER) e[f] = quelle[f] === true;
  return { entwurf: e as unknown as MvEntwurf, staende, kautionAn: !!m.kaution_art || !!m.kaution };
}

/** Payload für mieter.update – Regeln wie im bisherigen Dialog. */
export function payloadAusEntwurf(e: MvEntwurf, staende: readonly Mietstand[], kautionAn: boolean, heute: string): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  // leere Felder bleiben null (nicht leerer Text) – wie unberührte Felder bisher
  for (const f of TEXT_FELDER) p[f] = e[f].trim() === '' ? null : e[f];
  for (const f of SCHALTER) p[f] = e[f];
  for (const f of ZAHL_FELDER) p[f] = leseZahl(e[f]);
  if (!p.mietbeginn) delete p.mietbeginn;

  // Korrekturen der vier Felder gehören in den aktuell gültigen Stand; mit Positionen
  // bleibt „weitere Kosten“ deren Summe.
  const akt = aktuellerStand([...staende], heute);
  if (akt) {
    const positionen = akt.positionen ?? [];
    const korrigiert: Mietstand = {
      ...akt,
      kaltmiete: Number(p.kaltmiete) || 0,
      betriebskosten: Number(p.nebenkosten_vorauszahlung) || 0,
      heizkosten: Number(p.heizkosten) || 0,
      weitereKosten: positionen.length ? summePositionen(positionen) : (Number(p.weitere_kosten) || 0),
    };
    p.weitere_kosten = korrigiert.weitereKosten;
    p.miet_historie = sortiereStaende(staende.map(z => (z.id === korrigiert.id ? korrigiert : z)));
  } else {
    p.miet_historie = [...staende];
  }

  if (!kautionAn) { p.kaution_art = null; p.kaution_faelligkeit = null; }
  return p;
}

/** Gesamtmiete aus den vier Feldern (Anzeige). */
export function gesamtAusEntwurf(e: Pick<MvEntwurf, 'kaltmiete' | 'nebenkosten_vorauszahlung' | 'heizkosten' | 'weitere_kosten'>): number {
  const z = (s: string) => leseZahl(s) ?? 0;
  return Math.round((z(e.kaltmiete) + z(e.nebenkosten_vorauszahlung) + z(e.heizkosten) + z(e.weitere_kosten)) * 100) / 100;
}

/** Mietstände sichern: sortieren und die vier Spiegelfelder auf den heute gültigen Stand setzen. */
export function historieUpdate(staende: readonly Mietstand[], heute: string): { miet_historie: Mietstand[]; spiegel: ReturnType<typeof spiegelFelder> | null } {
  const sortiert = sortiereStaende([...staende]);
  const akt = aktuellerStand(sortiert, heute);
  return { miet_historie: sortiert, spiegel: akt ? spiegelFelder(akt) : null };
}

/** Vorlage für eine neue Mietänderung: Beträge und Positionen des aktuellen Stands. */
export function neueAenderung(akt: Mietstand | null, e: MvEntwurf, jetzt: number): Mietstand {
  const z = (s: string) => leseZahl(s) ?? 0;
  return {
    id: `m-${jetzt}`,
    gueltigAb: '',
    kaltmiete: akt?.kaltmiete ?? z(e.kaltmiete),
    betriebskosten: akt?.betriebskosten ?? z(e.nebenkosten_vorauszahlung),
    heizkosten: akt?.heizkosten ?? z(e.heizkosten),
    weitereKosten: akt?.weitereKosten ?? z(e.weitere_kosten),
    positionen: (akt?.positionen ?? []).map((x, i) => ({ ...x, id: `p-${jetzt}-${i}` })),
    notiz: null,
  };
}
